import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { Spin, Table, Alert, Modal, Badge, Button, Icon } from "antd";
import moment from "moment";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
`;

const Title = styled.div`
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
`;

const Section = styled.div`
  background: white;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #fd7e14;
`;

const FileInputWrapper = styled.div`
  margin: 15px 0;
`;

const FileLabel = styled.label`
  display: inline-block;
  padding: 10px 20px;
  background-color: #f5f5f5;
  border: 2px dashed #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #fd7e14;
    background-color: #fff7f0;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ButtonWrapper = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const OrangeButton = styled.button`
  background-color: #fd7e14;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background-color: #e66d0a;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const RefreshButton = styled(Button)`
  background-color: #fff;
  border-color: #fd7e14;
  color: #fd7e14;

  &:hover:not(:disabled) {
    background-color: #fff7f0;
    border-color: #e66d0a;
    color: #e66d0a;
  }
`;

const baseapi = process.env.REACT_APP_API + "/api";

const HaleonEvents = ({ token }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [weekNumber, setWeekNumber] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [file, setFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadResponse, setUploadResponse] = useState(null);

  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [sendMessage, setSendMessage] = useState("");
  const [sendResponse, setSendResponse] = useState(null);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const result = await axios.post(
        `${baseapi}/sidely/haleon-pharma/events`,
        {
          match: {
            Start: {
              $gte: {
                $date: moment()
                  .add(1, "weeks")
                  .startOf("week")
                  .toDate()
              },
              $lte: {
                $date: moment()
                  .add(1, "weeks")
                  .endOf("week")
                  .toDate()
              }
            }
          },
          page: page,
          pageSize: pageSize
        },
        {
          headers: {
            Authorization: token
          }
        }
      );

      setEvents(result.data.docs || []);
      setTotal(result.data.total || 0);
      setWeekNumber(result.data.weekNumber || "");
    } catch (e) {
      console.error("Error fetching events:", e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, [page, pageSize]);

  const handleFileChange = e => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFile(files[0]);
      setUploadSuccess(false);
      setUploadError(false);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadLoading(true);
    setUploadMessage("Importation en cours...");
    setUploadError(false);
    setUploadSuccess(false);
    setUploadResponse(null);

    try {
      const result = await axios.post(`${baseapi}/sidely/haleon-pharma/events/import`, formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data"
        }
      });

      setUploadLoading(false);
      setUploadSuccess(true);
      setUploadError(false);
      setUploadResponse(result.data);
      setUploadMessage(result.data.message || "Importation réussie");
      setFile(null);
      setPage(1);

      fetchEvents();
    } catch (e) {
      const response = e.response || {};
      const data = response.data || {};
      const errorMsg = (data && data.message) || data || "Une erreur est survenue";

      setUploadLoading(false);
      setUploadError(true);
      setUploadSuccess(false);
      setUploadMessage(errorMsg);
      setUploadResponse(response.data);
    }
  };

  const handleSendNotifications = async () => {
    setSendLoading(true);
    setSendError(false);
    setSendSuccess(false);
    setSendMessage("");
    setSendResponse(null);

    try {
      const result = await axios.post(
        `${baseapi}/sidely/haleon-pharma/events/send-planning-notifications`,
        {},
        {
          headers: {
            Authorization: token
          }
        }
      );

      setSendLoading(false);
      setSendSuccess(true);
      setSendResponse(result.data);
      setSendMessage(result.data.message || "Notifications envoyées avec succès");
      setSendModalVisible(false);
      setPage(1);

      fetchEvents();
    } catch (e) {
      const response = e.response || {};
      const data = response.data || {};
      const errorMsg = (data && data.message) || data || "Une erreur est survenue";

      setSendLoading(false);
      setSendError(true);
      setSendMessage(errorMsg);
      setSendResponse(response.data);
    }
  };

  const renderUploadSection = () => {
    return (
      <Section>
        <SectionTitle>Importation de fichier Excel</SectionTitle>

        {uploadError && (
          <Alert
            message="Erreur d'importation"
            description={uploadMessage}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 15 }}
            onClose={() => setUploadError(false)}
          />
        )}

        {uploadSuccess && uploadResponse && (
          <Alert
            message="Importation réussie"
            description={
              <div>
                <div>{uploadMessage}</div>
                <div style={{ marginTop: 10 }}>
                  <strong>Total d'événements:</strong> {uploadResponse.totalEvents || 0}
                  <br />
                  <strong>Événements traités:</strong> {uploadResponse.processedCount || 0}
                  <br />
                  {uploadResponse.skippedCount > 0 && (
                    <span>
                      <strong>Événements ignorés:</strong> {uploadResponse.skippedCount}
                    </span>
                  )}
                </div>
              </div>
            }
            type="success"
            showIcon
            closable
            style={{ marginBottom: 15 }}
            onClose={() => setUploadSuccess(false)}
          />
        )}

        <div>
          Choisissez un fichier Excel (.xlsx) contenant les événements Haleon Pharma. Le fichier doit contenir une
          feuille nommée "Events" avec les colonnes appropriées.
        </div>

        <FileInputWrapper>
          <FileInput type="file" id="haleon-file" accept=".xlsx,.xls" onChange={handleFileChange} />
          <FileLabel htmlFor="haleon-file">
            <Icon type="upload" style={{ marginRight: 8 }} />
            {file ? file.name : "Choisir un fichier Excel"}
          </FileLabel>
        </FileInputWrapper>

        <ButtonWrapper>
          <OrangeButton onClick={handleFileUpload} disabled={!file || uploadLoading}>
            {uploadLoading ? (
              <React.Fragment>
                <Icon type="loading" style={{ marginRight: 8 }} />
                Importation en cours...
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Icon type="upload" style={{ marginRight: 8 }} />
                Importer le fichier
              </React.Fragment>
            )}
          </OrangeButton>

          {file && !uploadLoading && <Button onClick={() => setFile(null)}>Annuler</Button>}
        </ButtonWrapper>
      </Section>
    );
  };

  const renderNotificationsSection = () => {
    return (
      <Section>
        <SectionTitle>Envoi des notifications</SectionTitle>

        {sendError && (
          <Alert
            message="Erreur d'envoi"
            description={sendMessage}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 15 }}
            onClose={() => setSendError(false)}
          />
        )}

        {sendSuccess && sendResponse && (
          <Alert
            message="Notifications envoyées"
            description={
              <div>
                <div>{sendMessage}</div>
                <div style={{ marginTop: 10 }}>
                  <strong>Emails envoyés:</strong> {sendResponse.emailsSent || 0}
                  <br />
                  <strong>Événements traités:</strong> {sendResponse.eventsProcessed || 0}
                  <br />
                  {sendResponse.eventsWithoutDp > 0 && (
                    <span style={{ color: "#fa8c16" }}>
                      <strong>Événements sans DP:</strong> {sendResponse.eventsWithoutDp}
                    </span>
                  )}
                  <br />
                  <strong>Semaine:</strong> {sendResponse.weekNumber || ""}
                </div>
              </div>
            }
            type="success"
            showIcon
            closable
            style={{ marginBottom: 15 }}
            onClose={() => setSendSuccess(false)}
          />
        )}

        <div style={{ marginBottom: 15 }}>
          Envoyer les notifications de planification aux DPs pour la semaine {weekNumber || "W+1"}.
        </div>

        <ButtonWrapper>
          <OrangeButton onClick={() => setSendModalVisible(true)} disabled={events.length === 0}>
            <Icon type="mail" style={{ marginRight: 8 }} />
            Envoyer les notifications de la semaine {weekNumber || "W+1"}
          </OrangeButton>
        </ButtonWrapper>

        <Modal
          title="Confirmation d'envoi"
          visible={sendModalVisible}
          onOk={handleSendNotifications}
          onCancel={() => setSendModalVisible(false)}
          okText="Envoyer"
          cancelText="Annuler"
          confirmLoading={sendLoading}
          okButtonProps={{ style: { backgroundColor: "#fd7e14", borderColor: "#fd7e14" } }}
        >
          <p>
            Vous êtes sur le point d'envoyer les notifications de planification aux DPs pour la semaine{" "}
            {weekNumber || "W+1"}.
          </p>
          <p>
            <strong>{events.length}</strong> événement(s) seront traités.
          </p>
          <p>Voulez-vous continuer ?</p>
        </Modal>
      </Section>
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80
    },
    {
      title: "Pharmacie",
      dataIndex: "Company_name",
      key: "Company_name",
      width: 200
    },
    {
      title: "Contact",
      dataIndex: "Contact_name",
      key: "Contact_name",
      width: 150
    },
    {
      title: "CDV",
      dataIndex: "Incharge_name",
      key: "Incharge_name",
      width: 150
    },
    {
      title: "Date de début",
      dataIndex: "Start",
      key: "Start",
      width: 150,
      render: start => {
        return start ? moment(start).format("DD/MM/YYYY HH:mm") : "-";
      }
    },
    {
      title: "DP",
      dataIndex: "Dp_name",
      key: "Dp_name",
      width: 150,
      render: dp_name => dp_name || "-"
    },
    {
      title: "Email DP",
      dataIndex: "Dp_email",
      key: "Dp_email",
      width: 200,
      render: dp_email => dp_email || "-"
    },
    {
      title: "Statut",
      dataIndex: "Sent_to_dp",
      key: "Sent_to_dp",
      width: 120,
      render: sent_to_dp => {
        return sent_to_dp ? <Badge status="success" text="Envoyé" /> : <Badge status="warning" text="Non envoyé" />;
      }
    }
  ];
  console.log(events);
  const renderEventsTable = () => {
    return (
      <Section>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
          <SectionTitle style={{ marginBottom: 0 }}>
            Liste des événements - Semaine {weekNumber || "W+1"} ({total} événement{total > 1 ? "s" : ""})
          </SectionTitle>
          <RefreshButton onClick={fetchEvents} disabled={isLoading}>
            <Icon type="reload" />
            Actualiser
          </RefreshButton>
        </div>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin tip="Chargement des événements..." />
          </div>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={events}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} événements`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                if (newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setPage(1);
                }
              }
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Section>
    );
  };

  return (
    <Container>
      <HeaderContainer>
        <Title>Gestion des événements Haleon Pharma</Title>
      </HeaderContainer>

      {renderUploadSection()}
      {renderNotificationsSection()}
      {renderEventsTable()}
    </Container>
  );
};

export default HaleonEvents;



// WEBPACK FOOTER //
// src/pages/sidely/HaleonEvents.js